import { createSlice } from '@reduxjs/toolkit';
import { initialUserPortfolio } from './user_portfolio.initial';
import { PortfolioProperty, PortfolioSlot, UserPortfolioState } from './user_portfolio.types';
import {
    cancelSlot,
    createHighlightedSlots,
    getPortfolio,
    getPortfolioFull,
    manageSlot,
    manageSlotPairs,
    updateValidity,
} from './thunks';

/** Convierte una fecha `YYYY-MM-DD` (input del formulario) a `DD-MM-YYYY` (formato de la tabla). */
const toDisplayDate = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) return isoDate;
    return `${day}-${month}-${year}`;
};

/** Cambio de ocupación de un slot: `propertyId` = id asignado, o `null` si se libera. */
interface SlotOccupancyChange {
    slotId: number;
    propertyId: number | null;
}

/**
 * Aplica de forma OPTIMISTA (sin volver a llamar al backend) el resultado de
 * asignar / liberar / reemplazar sobre el estado en memoria. El backend ya
 * confirmó la operación, así que reflejamos el cambio directamente:
 *   - `full.slots` y `portfolio.slots`: se actualiza `property_id` del slot.
 *   - `full.properties` y `portfolio.properties`: se recalcula `highlighted`
 *     según si la propiedad sigue ocupando algún slot (fuente de verdad = el
 *     conjunto completo `full` si está cargado; si no, la página de Vigencias).
 * Evita las recargas de `commercial_panel/portfolio` tras cada mutación.
 */
const applyOptimisticSlotChanges = (
    state: UserPortfolioState,
    changes: SlotOccupancyChange[],
): void => {
    if (changes.length === 0) return;

    const changeById = new Map<number, number | null>();
    changes.forEach((change) => changeById.set(change.slotId, change.propertyId));

    // Propiedades afectadas: las que salen (property_id previo) y las que entran.
    const affectedPropertyIds = new Set<number>();
    const applyToSlots = (slots: PortfolioSlot[] | undefined): void => {
        (slots ?? []).forEach((slot) => {
            if (!changeById.has(slot.id)) return;
            const nextPropertyId = changeById.get(slot.id) ?? null;
            if (slot.property_id != null) affectedPropertyIds.add(slot.property_id);
            if (nextPropertyId != null) affectedPropertyIds.add(nextPropertyId);
            slot.property_id = nextPropertyId;
        });
    };
    applyToSlots(state.full?.slots);
    applyToSlots(state.portfolio?.slots);

    if (affectedPropertyIds.size === 0) return;

    const referenceSlots = state.full?.loadedForParameter
        ? state.full.slots
        : state.portfolio?.slots ?? [];
    const isReferenced = (propertyId: number): boolean =>
        referenceSlots.some((slot) => slot.property_id === propertyId);

    const applyToProperties = (properties: PortfolioProperty[] | undefined): void => {
        (properties ?? []).forEach((property) => {
            if (affectedPropertyIds.has(property.id_property)) {
                property.highlighted = isReferenced(property.id_property) ? 1 : 0;
            }
        });
    };
    applyToProperties(state.full?.properties);
    applyToProperties(state.portfolio?.properties);
};

export const userPortfolioSlice = createSlice({
    name: 'user_portfolio',
    initialState: initialUserPortfolio,
    reducers: {
        clearPortfolio: (state) => {
            state.portfolio = initialUserPortfolio.portfolio;
            state.full = initialUserPortfolio.full;
        },
        clearValiditySaveError: (state) => {
            state.portfolio.saveError = null;
        },
        clearAssignError: (state) => {
            state.portfolio.assignError = null;
        },
        clearCreateError: (state) => {
            state.portfolio.createError = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getPortfolio.pending, (state) => {
            state.portfolio.loading = true;
            state.portfolio.error = null;
        });
        builder.addCase(getPortfolio.fulfilled, (state, action) => {
            const { data, page, pageSize, parameter, slotId, propertyId, slotsOrder, scope } =
                action.payload;
            const effectiveScope = scope ?? 'all';
            state.portfolio.loading = false;
            state.portfolio.data_user = data.data_user;
            state.portfolio.userType = data.userType;
            state.portfolio.loadedForParameter = parameter || null;
            if (pageSize != null) {
                state.portfolio.pageSize = pageSize;
            }

            // El backend pagina slots y propiedades con un único `page`, así que
            // cada sección se actualiza por separado para poder paginarlas de
            // forma independiente sin pisar la otra tabla.
            if (effectiveScope === 'slots' || effectiveScope === 'all') {
                state.portfolio.slots = data.slots;
                state.portfolio.totalSlots = data.totalSlots;
                state.portfolio.page = page ?? 0;
                state.portfolio.slotFilter = slotId ?? '';
                state.portfolio.slotsOrder = slotsOrder ?? null;
            }
            if (effectiveScope === 'properties' || effectiveScope === 'all') {
                state.portfolio.properties = data.properties;
                state.portfolio.totalProperties = data.totalProperties;
                state.portfolio.totalStatus = data.totalStatus;
                state.portfolio.propertyPage = page ?? 0;
                state.portfolio.propertyFilter = propertyId ?? '';
            }
        });
        builder.addCase(getPortfolio.rejected, (state, action) => {
            state.portfolio.loading = false;
            state.portfolio.error =
                action.error.message ?? 'Error cargando el portafolio del usuario';
        });

        // ── Carga completa (Asignaciones) ────────────────────────────────────
        builder.addCase(getPortfolioFull.pending, (state) => {
            state.full.loading = true;
            state.full.error = null;
        });
        builder.addCase(getPortfolioFull.fulfilled, (state, action) => {
            const { parameter, slots, properties, totalSlots, totalProperties } = action.payload;
            state.full.loading = false;
            state.full.slots = slots;
            state.full.properties = properties;
            state.full.totalSlots = totalSlots;
            state.full.totalProperties = totalProperties;
            state.full.loadedForParameter = parameter || null;
        });
        builder.addCase(getPortfolioFull.rejected, (state, action) => {
            state.full.loading = false;
            state.full.error =
                action.error.message ?? 'Error cargando el portafolio completo del usuario';
        });

        builder.addCase(updateValidity.pending, (state) => {
            state.portfolio.saving = true;
            state.portfolio.saveError = null;
        });
        builder.addCase(updateValidity.fulfilled, (state, action) => {
            state.portfolio.saving = false;
            // Actualización local optimista: el backend ya confirmó el cambio, así
            // que reflejamos la nueva fecha de expiración en los slots afectados sin
            // volver a llamar a `commercial_panel/portfolio` (que puede tardar varios
            // segundos). Los meses se dejan intactos a propósito.
            const arg = action.meta?.arg;
            if (!arg) return;
            const { slotIds, expirationDate } = arg;
            const displayDate = toDisplayDate(expirationDate);
            state.portfolio.slots = state.portfolio.slots.map((slot) =>
                slotIds.includes(slot.id) ? { ...slot, date_end: displayDate } : slot
            );
        });
        builder.addCase(updateValidity.rejected, (state, action) => {
            state.portfolio.saving = false;
            state.portfolio.saveError =
                action.error.message ?? 'Error al cambiar la vigencia';
        });

        builder.addCase(manageSlot.pending, (state) => {
            state.portfolio.assigning = true;
            state.portfolio.assignError = null;
        });
        builder.addCase(manageSlot.fulfilled, (state, action) => {
            state.portfolio.assigning = false;
            const arg = action.meta?.arg;
            if (!arg) return;
            const { slotIds, propertyId } = arg;
            applyOptimisticSlotChanges(
                state,
                slotIds.map((slotId) => ({ slotId, propertyId })),
            );
        });
        builder.addCase(manageSlot.rejected, (state, action) => {
            state.portfolio.assigning = false;
            state.portfolio.assignError =
                action.error.message ?? 'Error al asignar el slot a la propiedad';
        });

        builder.addCase(manageSlotPairs.pending, (state) => {
            state.portfolio.assigning = true;
            state.portfolio.assignError = null;
        });
        builder.addCase(manageSlotPairs.fulfilled, (state, action) => {
            state.portfolio.assigning = false;
            const arg = action.meta?.arg;
            if (!arg) return;
            applyOptimisticSlotChanges(
                state,
                arg.pairs.map((pair) => ({ slotId: pair.slotId, propertyId: pair.propertyId })),
            );
        });
        builder.addCase(manageSlotPairs.rejected, (state, action) => {
            state.portfolio.assigning = false;
            state.portfolio.assignError =
                action.error.message ?? 'Error al asignar las propiedades a los slots';
        });

        builder.addCase(cancelSlot.pending, (state) => {
            state.portfolio.assigning = true;
            state.portfolio.assignError = null;
        });
        builder.addCase(cancelSlot.fulfilled, (state, action) => {
            state.portfolio.assigning = false;
            const arg = action.meta?.arg;
            if (!arg) return;
            applyOptimisticSlotChanges(
                state,
                arg.slotIds.map((slotId) => ({ slotId, propertyId: null })),
            );
        });
        builder.addCase(cancelSlot.rejected, (state, action) => {
            state.portfolio.assigning = false;
            state.portfolio.assignError =
                action.error.message ?? 'Error al liberar el slot';
        });

        builder.addCase(createHighlightedSlots.pending, (state) => {
            state.portfolio.creating = true;
            state.portfolio.createError = null;
        });
        builder.addCase(createHighlightedSlots.fulfilled, (state) => {
            state.portfolio.creating = false;
        });
        builder.addCase(createHighlightedSlots.rejected, (state, action) => {
            state.portfolio.creating = false;
            state.portfolio.createError =
                action.error.message ?? 'Error al crear los slots comerciales';
        });
    },
});

export const {
    clearPortfolio,
    clearValiditySaveError,
    clearAssignError,
    clearCreateError,
} = userPortfolioSlice.actions;

export default userPortfolioSlice.reducer;
